import type { ScheduleResult } from "@planerius/contracts/cabinet"

import { shiftDate, todayInZone } from "../../../lib/format"

/**
 * 3-Б8 «График смен» — ЧЕТЫРЕ ВИДА ДНЯ И ПЯТЫЙ, НЕЙТРАЛЬНЫЙ.
 *
 * Почему это отдельная чистая функция, а не раскраска в разметке: виды дня различаются
 * не оттенком, а СМЫСЛОМ, и путать их дороже всего в одном месте — «правка поверх
 * шаблона» против «смена из шаблона». Первая при развороте шаблона на следующий месяц
 * НЕ затирается, вторая пересчитывается целиком. Администратор, который этого не видит,
 * ставит правку, разворачивает шаблон и теряет её — а узнаёт в день, когда мастер
 * не вышел.
 *
 * ПЯТЫЙ ВИД — ВЫХОДНОЙ — намеренно нейтрален и ничего не сообщает: у мастера по графику
 * 2/2 половина месяца выходные, и красить их в «нет данных» значит залить полсетки
 * тревогой на ровном месте.
 *
 * ПРАЗДНИК — ПОДСКАЗКА, А НЕ РЕШЕНИЕ. calendarHint приходит из производственного
 * календаря; решает салон (ADR-007 п.9). Поэтому у праздничной клетки стоит вопрос,
 * а не готовый ответ, и она не отменяет ни одной смены сама.
 */

export type ScheduleDayKind =
  /** Смена из шаблона: «10–20 · из шаблона». */
  | "shift"
  /** Ручная правка поверх шаблона. При развороте шаблона не затирается. */
  | "override"
  /** Отсутствие: отпуск, больничный, отгул. */
  | "timeoff"
  /** Праздник по календарю — подсказка, решает салон. */
  | "holiday"
  /** Выходной. Нейтральный, молчит. */
  | "none"

export interface ScheduleCell {
  readonly date: string
  readonly kind: ScheduleDayKind
  /** Главная надпись: «10–20», «отпуск», «празд.». Пусто у выходного. */
  readonly primary: string
  /** Приписка: «укор.», «?». Пусто, когда её нет. */
  readonly secondary: string
  /** Подсказка при наведении. У каждого вида своя и объясняет именно вид. */
  readonly title: string
}

export interface ScheduleRow {
  readonly employeeId: string
  readonly name: string
  readonly color: string | null
  readonly cells: readonly ScheduleCell[]
}

export interface ScheduleDayHead {
  readonly date: string
  /** «Пн», «Вт», … */
  readonly weekdayLabel: string
  /** Число месяца. */
  readonly dayNumber: number
  readonly isToday: boolean
  readonly isSunday: boolean
}

export interface ScheduleWeekView {
  readonly days: readonly ScheduleDayHead[]
  readonly rows: readonly ScheduleRow[]
  /**
   * Дни, когда филиал открыт, онлайн-запись работает, а на смене нет НИ ОДНОГО мастера.
   * Это не украшение графика: клиент в такой день видит пустой календарь и уходит.
   */
  readonly emptyOpenDays: readonly ScheduleDayHead[]
}

const WEEKDAY_LABELS: readonly string[] = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]

/** Понедельник недели, в которую попала дата. Неделя салона начинается с понедельника. */
export function weekStart(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  // getUTCDay: 0 = воскресенье. Для воскресенья отступаем на шесть дней назад,
  // иначе неделя «15–21 сентября» для 21-го числа уехала бы на следующую.
  const weekday = parsed.getUTCDay()
  const back = weekday === 0 ? 6 : weekday - 1
  return shiftDate(date, -back)
}

export function weekDates(start: string): readonly string[] {
  return Array.from({ length: 7 }, (_, index) => shiftDate(start, index))
}

/**
 * «14–20 сентября» и «19 сентября».
 *
 * ПОЧЕМУ ОДИН ФОРМАТ НА ДВА МЕСТА И ПОЧЕМУ С ЧИСЛОМ. В русском у месяца две формы:
 * сам по себе он «сентябрь», а при числе — «сентября». Intl выдаёт первую, если
 * попросить только месяц, и вторую — если попросить день вместе с месяцем. Поэтому
 * месяц НИКОГДА не форматируется отдельно: «14–20 сентябрь» читается как опечатка,
 * и заметить её в коде глазами невозможно — она появляется только на экране.
 */
const DAY_MONTH = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  timeZone: "UTC",
})

function dayMonth(date: string): string {
  return DAY_MONTH.format(new Date(`${date}T12:00:00Z`))
}

/** «14–20 сентября» либо «28 сентября – 4 октября», когда неделя переходит месяц. */
export function formatWeekRange(start: string): string {
  const end = shiftDate(start, 6)
  const fromDay = new Date(`${start}T12:00:00Z`).getUTCDate()
  const sameMonth = start.slice(0, 7) === end.slice(0, 7)
  return sameMonth ? `${fromDay}–${dayMonth(end)}` : `${dayMonth(start)} – ${dayMonth(end)}`
}

/** «10:00» → «10», «09:30» → «9:30». В клетке 34 px лишний ноль съедает место. */
function shortHour(instant: string, timeZone: string): string {
  const format = new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  })
  const [hours = "00", minutes = "00"] = format.format(new Date(instant)).split(":")
  const hour = String(Number(hours))
  return minutes === "00" ? hour : `${hour}:${minutes}`
}

function minutesBetween(startAt: string, endAt: string): number {
  return (new Date(endAt).getTime() - new Date(startAt).getTime()) / 60000
}

/** Все календарные даты филиала, которые задевает интервал. Конец полуоткрытый. */
function datesOfInterval(startAt: string, endAt: string, timeZone: string): readonly string[] {
  const from = todayInZone(timeZone, new Date(startAt))
  // Минус миллисекунда: отсутствие «до полуночи» не должно захватывать следующий день.
  const to = todayInZone(timeZone, new Date(new Date(endAt).getTime() - 1))
  const out: string[] = [from]
  let cursor = from
  // Ограничение сверху — защита от кривого интервала в данных: сетка на неделю,
  // и перебирать год из-за одной строки отпуска «до 2099» она не должна.
  for (let step = 0; step < 400 && cursor < to; step += 1) {
    cursor = shiftDate(cursor, 1)
    out.push(cursor)
  }
  return out
}

export function buildScheduleWeek(
  result: ScheduleResult,
  start: string,
  today: string,
): ScheduleWeekView {
  const timeZone = result.branch.timezone
  const dates = weekDates(start)

  const days: readonly ScheduleDayHead[] = dates.map((date) => {
    const parsed = new Date(`${date}T12:00:00Z`)
    const weekday = parsed.getUTCDay()
    return {
      date,
      weekdayLabel: WEEKDAY_LABELS[weekday] ?? "",
      dayNumber: parsed.getUTCDate(),
      isToday: date === today,
      isSunday: weekday === 0,
    }
  })

  const dayByDate = new Map(result.days.map((day) => [day.date, day]))

  // Отсутствия раскладываются по календарным датам филиала: отпуск приходит одним
  // интервалом на две недели, а сетке нужна клетка на каждый день.
  const timeOffByEmployee = new Map<string, Map<string, string | null>>()
  for (const block of result.busy) {
    if (block.kind !== "timeoff" || block.employeeId === null) continue
    const forEmployee = timeOffByEmployee.get(block.employeeId) ?? new Map<string, string | null>()
    for (const date of datesOfInterval(block.startAt, block.endAt, timeZone)) {
      forEmployee.set(date, block.note)
    }
    timeOffByEmployee.set(block.employeeId, forEmployee)
  }

  const workdaysByEmployee = new Map<string, Map<string, (typeof result.workdays)[number]>>()
  for (const workday of result.workdays) {
    const forEmployee =
      workdaysByEmployee.get(workday.employeeId) ??
      new Map<string, (typeof result.workdays)[number]>()
    forEmployee.set(workday.date, workday)
    workdaysByEmployee.set(workday.employeeId, forEmployee)
  }

  const rows: readonly ScheduleRow[] = result.employees.map((employee) => {
    const workdays = workdaysByEmployee.get(employee.id) ?? new Map()
    const timeOff = timeOffByEmployee.get(employee.id) ?? new Map<string, string | null>()

    // Эталон длины смены — по шаблонным дням ЭТОГО мастера на этой же неделе.
    // Без него «укор.» пришлось бы назначать по среднему салона, а у колориста
    // и барбера дни разной длины по устройству работы, а не по правке.
    const patternMinutes = [...workdays.values()]
      .filter((workday) => workday.source === "pattern")
      .map((workday) => minutesBetween(workday.startAt, workday.endAt))
    const typical =
      patternMinutes.length === 0
        ? null
        : patternMinutes.toSorted((a, b) => a - b)[Math.floor(patternMinutes.length / 2)] ?? null

    const cells = dates.map((date): ScheduleCell => {
      const workday = workdays.get(date)
      const off = timeOff.get(date)
      const day = dayByDate.get(date)

      // Порядок ветвей — это и есть приоритет видов. Отсутствие сильнее смены:
      // мастер в отпуске не работает, даже если шаблон говорит обратное.
      if (off !== undefined) {
        const label = off === null || off.trim().length === 0 ? "отсутствие" : off.trim()
        return {
          date,
          kind: "timeoff",
          primary: label.length > 10 ? `${label.slice(0, 9)}…` : label,
          secondary: "",
          title: label,
        }
      }

      if (workday !== undefined) {
        const span = `${shortHour(workday.startAt, timeZone)}–${shortHour(workday.endAt, timeZone)}`
        if (workday.source === "pattern") {
          return {
            date,
            kind: "shift",
            primary: span,
            secondary: "",
            title: `${span} · из шаблона`,
          }
        }
        const length = minutesBetween(workday.startAt, workday.endAt)
        const mark =
          typical === null ? "правка" : length < typical ? "укор." : length > typical ? "удлин." : "правка"
        return {
          date,
          kind: "override",
          primary: span,
          secondary: mark,
          title: "Правка поверх шаблона — при развороте не затрётся",
        }
      }

      const hint = day?.calendarHint ?? null
      if (hint !== null && (hint.dayType === "holiday" || hint.dayType === "nonworking")) {
        return {
          date,
          kind: "holiday",
          primary: "празд.",
          secondary: "?",
          title: "Праздник по календарю — закрыть / как обычно / особый график?",
        }
      }

      return { date, kind: "none", primary: "", secondary: "", title: "Выходной" }
    })

    return {
      employeeId: employee.id,
      name: employee.displayName,
      color: employee.color,
      cells,
    }
  })

  const emptyOpenDays = days.filter((head) => {
    const day = dayByDate.get(head.date)
    if (day === undefined || day.isClosed) return false
    return !rows.some((row) =>
      row.cells.some((cell) => cell.date === head.date && (cell.kind === "shift" || cell.kind === "override")),
    )
  })

  return { days, rows, emptyOpenDays }
}

/**
 * «Пт, 19 сентября» — для предупреждения под сеткой.
 *
 * День недели назван словом не для красоты: предупреждение читают в отрыве от сетки,
 * а «19-е» без дня недели не подсказывает, насколько это скоро.
 */
export function formatDayWarning(head: ScheduleDayHead, allDates: readonly string[]): string {
  const parsed = new Date(`${head.date}T12:00:00Z`)
  const weekday = new Intl.DateTimeFormat("ru-RU", { weekday: "short", timeZone: "UTC" }).format(parsed)
  const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1)
  // allDates нужен затем, чтобы не указывать на клетку вне показанной недели:
  // предупреждение обязано вести туда, куда человек может ткнуть.
  return allDates.includes(head.date)
    ? `${capitalized}, ${dayMonth(head.date)}`
    : dayMonth(head.date)
}
